import { useState } from 'react';
import { uploadNocobaseAttachments } from '../services/api';
import { FormProvider as ContextProvider } from '../components/forms/shared/FormContext';

/**
 * A shared hook for managing common form state across support portal forms.
 * Provides the formData object and standard handlers for text inputs and file attachments.
 *
 * @param {object} initialState - The initial state of the form
 * @returns {object} { formData, setFormData, handleChange, handleAddFiles, handleRemoveFile, touched, setTouched, handleBlur, setAllTouched, FormProvider }
 */
export function useFormState(initialState) {
  const [formData, setFormData] = useState(initialState);
  const [touched, setTouched] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const setAllTouched = () => {
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
  };

  const handleAddFiles = async (newFiles) => {
    // 1. Initialise the newly dropped files as "uploading"
    const newAttachments = newFiles.map(file => ({
      file,
      id: null,
      url: null,
      isUploading: true,
      error: null
    }));

    setFormData((prev) => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments],
    }));

    // 2. Perform the upload in the background
    try {
      const results = await uploadNocobaseAttachments(newFiles);
      
      setFormData((prev) => {
        // Map over current attachments, if it's one of the new files, update it
        const nextAttachments = prev.attachments.map(att => {
          // Identify the newly uploaded files by comparing the exact File object
          const index = newFiles.indexOf(att.file);
          if (index !== -1 && results[index]) {
            return {
              ...att,
              id: results[index].id,
              url: results[index].url,
              isUploading: false
            };
          }
          return att;
        });
        return { ...prev, attachments: nextAttachments };
      });
    } catch (err) {
      setFormData((prev) => {
        const nextAttachments = prev.attachments.map(att => {
          if (newFiles.includes(att.file)) {
            return { ...att, isUploading: false, error: err.message };
          }
          return att;
        });
        return { ...prev, attachments: nextAttachments };
      });
    }
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const contextValue = { formData, touched, handleBlur, setAllTouched };

  return {
    formData,
    setFormData,
    handleChange,
    handleAddFiles,
    handleRemoveFile,
    touched,
    setTouched,
    handleBlur,
    setAllTouched,
    contextValue,
  };
}
